/* ULTREIA — personalizador de variantes.
   Interpreta las variantes que YA existen en Shopify y las presenta
   como una hoja de pedido. No crea, borra, renombra ni cambia precios
   ni ids: lee /products/{handle}.js (o el producto que le pasa la carta)
   y mapea la elección al id real de la variante.
   - Muestra selector solo si hay una opción con 2+ valores.
   - Combinación inexistente o agotada → no se puede agregar.
   - Precio y disponibilidad salen de la variante encontrada.

   ALCANCE (desde v13): productos con el tag "carta" y la gift card.
   Los demás usan el selector y el botón nativos de Shopify, que
   respetan el formulario de regalo. La gift card trae adentro
   "Enviarla como regalo" y manda los datos con los nombres que Shopify
   espera (Recipient email, Recipient name, Message, Send on y
   __shopify_send_gift_card_to_recipient).

   CAMBIOS v16 (octubre 2026)
   · Arreglado el choque que rompía "Agregar al carrito" en toda ficha
     sin selector (libros, espresso, Just Fit): la marca de "botón ya
     revisado" usaba el mismo atributo que el disparador de la home
     (data-u-perso) y el clic terminaba en /products/undefined. La marca
     ahora es data-u-perso-listo y el disparador exige un handle real.
   · Es un <dialog> nativo: el fondo queda inerte, Escape cierra, el
     foco entra al abrir y vuelve al botón que lo abrió. Las opciones son
     radios de verdad (se anuncian y se recorren con flechas) y elegir
     una ya no redibuja el panel: solo se actualiza lo que cambió, así el
     foco no se pierde.
   · Abre con la primera combinación disponible elegida (antes abría en
     "Elegí una opción" aunque "Común" fuera la obvia).
   · Precios con el formato de la tienda (el mismo que escribe Liquid),
     no con Intl: antes el panel decía "$ 280,00" y la carta "$280".
   · API para la carta (window.Ultreia.personalizar): abrir el panel con
     un producto ya cargado, agregar sin abrir el cajón y refrescar el
     contador. Así hay un solo panel en todo el sitio.
   · Los estilos viven en u-personalizar.css (antes, dentro de
     ultreia.css, que ya no se carga). Si la página no lo trae, se pide
     la primera vez que se abre el panel. */
(function () {
  'use strict';

  var TAG_CARTA = 'carta';
  var guion = document.currentScript;

  /* ── Dinero: mismo formato que el filtro money de Liquid ─────────── */
  function formatoDinero() {
    // Formato uruguayo en todo el sitio: "$ 1.100" (la tienda tiene "$1,100").
    return '$ {{amount_no_decimals_with_comma_separator}}';
  }
  function dinero(centavos) {
    var c = Math.round(Number(centavos) || 0);
    var f = formatoDinero();
    var m = f.match(/\{\{\s*(\w+)\s*\}\}/);
    function armar(dec, mil, sep) {
      var p = (c / 100).toFixed(dec).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, mil);
      return p[0] + (p[1] ? sep + p[1] : '');
    }
    var v;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': v = armar(0, ',', '.'); break;
      case 'amount_with_comma_separator': v = armar(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = armar(0, '.', ','); break;
      case 'amount_with_space_separator': v = armar(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': v = armar(0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': v = armar(2, "'", '.'); break;
      default: v = armar(2, ',', '.');
    }
    return m ? f.replace(m[0], v) : v;
  }
  function esc(t) {
    return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── Productos ───────────────────────────────────────────────────── */
  var cache = {};
  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) cache[handle] = p; return p; })
      .catch(function () { return null; });
  }

  function optionNames(p) {
    return (p.options || []).map(function (o) { return (typeof o === 'string') ? o : o.name; });
  }
  function distinctValues(p, i) {
    var out = [];
    p.variants.forEach(function (v) { if (out.indexOf(v.options[i]) === -1) out.push(v.options[i]); });
    return out;
  }
  function esGiftCard(p) {
    return !!(p && (p.gift_card || /gift\s*card/i.test(p.type || '')));
  }
  function esDeLaCarta(p) {
    if (!p || esGiftCard(p)) return false;
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    return tags.indexOf(TAG_CARTA) !== -1;
  }
  function tieneOpciones(p) {
    if (!p || !p.variants || p.variants.length < 2) return false;
    return optionNames(p).some(function (n, i) { return distinctValues(p, i).length >= 2; });
  }
  function needsPicker(p) {
    if (!esDeLaCarta(p) && !esGiftCard(p)) return false;
    return tieneOpciones(p);
  }
  function findVariant(p, sel) {
    return p.variants.find(function (v) {
      return sel.every(function (s, i) { return s != null && v.options[i] === s; });
    });
  }
  function valueMatches(p, i, val, sel, requireAvailable) {
    return p.variants.some(function (v) {
      if (v.options[i] !== val) return false;
      if (requireAvailable && !v.available) return false;
      return sel.every(function (s, j) { return j === i || s == null || v.options[j] === s; });
    });
  }
  /* Diferencia de precio de un valor respecto del más barato del
     producto ("Almendras +$80"). En la gift card no: el valor ES el monto. */
  function delta(p, i, val) {
    var base = Math.min.apply(null, p.variants.map(function (v) { return Number(v.price); }));
    var min = null;
    p.variants.forEach(function (v) { if (v.options[i] === val) { var n = Number(v.price); if (min === null || n < min) min = n; } });
    return min === null ? 0 : min - base;
  }

  /* ── Hoja de estilos (solo si la página no la trajo) ─────────────── */
  function asegurarEstilos() {
    if (document.querySelector('link[href*="u-personalizar.css"]')) return;
    var href = (guion && guion.getAttribute('data-css')) || (guion && guion.src ? guion.src.split('?')[0].replace('ultreia-personalizar.js', 'u-personalizar.css') : '');
    if (!href) return;
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    document.head.appendChild(l);
  }

  /* ── Carrito ─────────────────────────────────────────────────────── */
  function seccionesCarrito() {
    var s = [];
    if (document.querySelector('cart-drawer')) s.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) s.push('cart-icon-bubble');
    return s;
  }
  function interior(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }
  /* Pinta el cajón y el contador con el HTML que devuelve Shopify junto
     con el agregado (mismo mecanismo que usa Dawn). abrir = false deja
     el cajón cerrado: la carta suma de a un ítem y no quiere taparse. */
  function pintarCarrito(secciones, abrir) {
    secciones = secciones || {};
    var burbuja = document.getElementById('cart-icon-bubble');
    if (burbuja && secciones['cart-icon-bubble']) {
      var b = interior(secciones['cart-icon-bubble'], '.shopify-section');
      if (b != null) burbuja.innerHTML = b;
    }
    var cajon = document.querySelector('cart-drawer');
    if (cajon && secciones['cart-drawer']) {
      var html = interior(secciones['cart-drawer'], '#CartDrawer');
      var destino = cajon.querySelector('#CartDrawer');
      if (html != null && destino) destino.innerHTML = html;
      cajon.classList.remove('is-empty');
      var inner = cajon.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      var velo = cajon.querySelector('#CartDrawer-Overlay');
      if (velo && typeof cajon.close === 'function') velo.addEventListener('click', cajon.close.bind(cajon));
    }
    if (!abrir) return;
    if (cajon && typeof cajon.open === 'function') { cajon.open(); return; }
    var link = document.querySelector('#cart-icon-bubble, .header__icon--cart');
    if (link) link.click(); else window.location = (window.routes && window.routes.cart_url) || '/cart';
  }
  function avisarDawn(id, respuesta) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ultreia-personalizar', productVariantId: id, cartData: respuesta });
      }
    } catch (e) { /* sin pubsub no pasa nada */ }
  }
  function mensajeError(j) {
    var msg = (j && (j.description || j.message)) || 'No se pudo agregar. Probá de nuevo.';
    if (typeof msg === 'object') msg = Object.keys(msg).map(function (k) { return msg[k]; }).join(' ');
    return String(msg);
  }
  /* agregar([{ id, quantity, properties }], { abrirCajon }) →
     Promise<{ ok, respuesta, error }>. Nunca rechaza. */
  function agregar(items, opciones) {
    opciones = opciones || {};
    var secciones = seccionesCarrito();
    var cuerpo = { items: items };
    if (secciones.length) { cuerpo.sections = secciones.join(','); cuerpo.sections_url = location.pathname; }
    return fetch(((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(cuerpo)
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok && !j.status, j: j }; }); })
      .then(function (res) {
        if (!res.ok) return { ok: false, error: mensajeError(res.j) };
        pintarCarrito(res.j.sections, opciones.abrirCajon !== false);
        avisarDawn(items[0] && items[0].id, res.j);
        return { ok: true, respuesta: res.j };
      })
      .catch(function () { return { ok: false, error: 'No se pudo agregar. Revisá la conexión y probá de nuevo.' }; });
  }
  function estadoCarrito() {
    return fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ── El panel ────────────────────────────────────────────────────── */
  var dialogo, hoja, disparador, alCerrar;
  function construir() {
    if (dialogo) return;
    asegurarEstilos();
    dialogo = document.createElement('dialog');
    dialogo.className = 'u-perso';
    dialogo.setAttribute('aria-labelledby', 'u-perso-titulo');
    dialogo.innerHTML = '<form class="u-perso__hoja" novalidate></form>';
    hoja = dialogo.querySelector('form');
    /* Clic en el velo (fuera de la hoja) cierra. */
    dialogo.addEventListener('click', function (e) { if (e.target === dialogo) cerrar(); });
    /* El <dialog> modal deja inerte la página, pero Tab igual puede irse a
       la barra del navegador: se da la vuelta adentro de la hoja. */
    dialogo.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = [].filter.call(hoja.querySelectorAll('button, input, textarea, select, a[href]'), function (el) {
        return !el.disabled && el.offsetParent !== null && !(el.type === 'radio' && !el.checked && hoja.querySelector('input[type="radio"][name="' + el.name + '"]:checked'));
      });
      if (!f.length) return;
      var primero = f[0], ultimo = f[f.length - 1];
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    });
    dialogo.addEventListener('close', function () {
      document.documentElement.classList.remove('u-perso-abierto');
      var d = disparador; disparador = null;
      if (d && d.isConnected && typeof d.focus === 'function') d.focus();
      var cb = alCerrar; alCerrar = null;
      if (cb) cb();
    });
    document.body.appendChild(dialogo);
  }
  function cerrar() { if (dialogo && dialogo.open) dialogo.close(); }

  /* abrir(producto | handle, opciones)
       opciones.variante   id de variante a dejar elegida
       opciones.forzar     true = abrir aunque el producto no tenga el tag
                           "carta" (la carta ya sabe que es de la carta)
       opciones.abrirCajon false = después de agregar, no abrir el cajón
       opciones.alAgregar  función(respuesta, variante, cantidad)
       opciones.disparador elemento al que vuelve el foco al cerrar */
  function abrir(producto, opciones) {
    opciones = opciones || {};
    var promesa = (typeof producto === 'string') ? getProduct(producto) : Promise.resolve(producto);
    var origen = opciones.disparador || document.activeElement;
    return promesa.then(function (p) {
      if (!p || !p.variants || !p.variants.length) return false;
      if (!(opciones.forzar ? tieneOpciones(p) : needsPicker(p))) return false;
      dibujar(p, opciones);
      disparador = origen;
      alCerrar = opciones.alCerrar || null;
      document.documentElement.classList.add('u-perso-abierto');
      if (typeof dialogo.showModal === 'function') dialogo.showModal(); else dialogo.setAttribute('open', '');
      var primero = hoja.querySelector('input[type="radio"]:checked') || hoja.querySelector('input[type="radio"]:not(:disabled)') || hoja.querySelector('.u-perso__titulo');
      if (primero) primero.focus();
      return true;
    });
  }

  function dibujar(p, opciones) {
    construir();
    var names = optionNames(p);
    var gift = esGiftCard(p);
    /* Elección inicial: la variante pedida, si no la primera disponible. */
    var pre = opciones.variante && p.variants.find(function (v) { return String(v.id) === String(opciones.variante); });
    if (!pre) pre = p.variants.find(function (v) { return v.available; }) || p.variants[0];
    var sel = pre.options.slice(0, names.length);
    var qty = 1;
    var uid = 'u-perso-' + (p.id || 'x');

    var img = p.featured_image ? (typeof p.featured_image === 'string' ? p.featured_image : p.featured_image.src || '') : '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    var desc = String(p.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (desc.length > 180) desc = desc.slice(0, 177).replace(/\s+\S*$/, '') + '…';

    var grupos = names.map(function (name, i) {
      var vals = distinctValues(p, i);
      if (vals.length < 2) return '';
      return '<fieldset class="u-perso__grupo"><legend class="u-perso__leyenda">' + esc(name) + '</legend><div class="u-perso__opciones">' +
        vals.map(function (val, k) {
          var d = gift ? 0 : delta(p, i, val);
          return '<label class="u-perso__opcion">' +
            '<input class="u-perso__radio" type="radio" name="' + uid + '-o' + i + '" value="' + esc(val) + '" data-oi="' + i + '" id="' + uid + '-o' + i + '-' + k + '">' +
            '<span class="u-perso__opcion-nombre">' + esc(val) + '</span>' +
            '<span class="u-perso__opcion-dato">' + (d > 0 ? '+' + esc(dinero(d)) : '') + '</span>' +
            '<span class="u-perso__opcion-estado" hidden>Agotado</span>' +
          '</label>';
        }).join('') + '</div></fieldset>';
    }).join('');

    var regalo = gift ? (
      '<fieldset class="u-perso__grupo u-perso__regalo">' +
        '<legend class="u-oculto">Enviarla como regalo</legend>' +
        '<label class="u-perso__check"><input type="checkbox" data-regalo> <span>Enviarla como regalo</span></label>' +
        '<div class="u-perso__campos" hidden>' +
          '<div class="u-campo"><label class="u-campo__rotulo" for="' + uid + '-email">Mail de quien la recibe</label><input class="u-entrada" id="' + uid + '-email" type="email" data-r="email" autocomplete="off" required aria-describedby="' + uid + '-error"></div>' +
          '<div class="u-campo"><label class="u-campo__rotulo" for="' + uid + '-nombre">Su nombre <span class="u-perso__opcional">(opcional)</span></label><input class="u-entrada" id="' + uid + '-nombre" type="text" data-r="nombre" maxlength="255"></div>' +
          '<div class="u-campo"><label class="u-campo__rotulo" for="' + uid + '-mensaje">Mensaje <span class="u-perso__opcional">(opcional, hasta 200 caracteres)</span></label><textarea class="u-entrada" id="' + uid + '-mensaje" data-r="mensaje" maxlength="200" rows="3"></textarea></div>' +
          '<div class="u-campo"><label class="u-campo__rotulo" for="' + uid + '-fecha">Enviar el <span class="u-perso__opcional">(opcional; si no, sale hoy)</span></label><input class="u-entrada" id="' + uid + '-fecha" type="date" data-r="fecha"></div>' +
          '<p class="u-campo__ayuda">Le llega por mail con el código. Si no marcás esta opción, te llega a vos.</p>' +
        '</div>' +
      '</fieldset>') : '';

    hoja.innerHTML =
      '<header class="u-perso__cabecera">' +
        (img ? '<div class="u-perso__foto u-medio u-medio--1x1"><img src="' + esc(img) + '" alt="" width="120" height="120"></div>' : '') +
        '<div class="u-perso__titulos">' +
          (p.type ? '<p class="u-rotulo">' + esc(p.type) + '</p>' : '') +
          '<h2 class="u-perso__titulo" id="u-perso-titulo" tabindex="-1">' + esc(p.title) + '</h2>' +
          (desc ? '<p class="u-perso__desc">' + esc(desc) + '</p>' : '') +
        '</div>' +
        '<button type="button" class="u-perso__cerrar" aria-label="Cerrar"><span aria-hidden="true">×</span></button>' +
      '</header>' +
      '<div class="u-perso__cuerpo">' + grupos + regalo +
        '<div class="u-perso__cantidad" role="group" aria-labelledby="' + uid + '-cant">' +
          '<span class="u-perso__leyenda" id="' + uid + '-cant">Cantidad</span>' +
          '<div class="u-perso__paso">' +
            '<button type="button" class="u-perso__menos" data-q="-1" aria-label="Uno menos"><span aria-hidden="true">−</span></button>' +
            '<output class="u-perso__cuantos u-num" aria-live="polite">1</output>' +
            '<button type="button" class="u-perso__mas" data-q="1" aria-label="Uno más"><span aria-hidden="true">+</span></button>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<footer class="u-perso__pie">' +
        '<p class="u-campo__error u-perso__error" id="' + uid + '-error" role="alert" hidden></p>' +
        '<button type="submit" class="u-btn u-btn--bloque u-perso__agregar"><span class="u-perso__agregar-texto">Agregar</span><span class="u-btn__precio u-perso__total"></span></button>' +
      '</footer>';

    var radios = hoja.querySelectorAll('.u-perso__radio');
    var btn = hoja.querySelector('.u-perso__agregar');
    var btnTexto = hoja.querySelector('.u-perso__agregar-texto');
    var total = hoja.querySelector('.u-perso__total');
    var cuantos = hoja.querySelector('.u-perso__cuantos');
    var menos = hoja.querySelector('.u-perso__menos');
    var error = hoja.querySelector('.u-perso__error');
    var chk = hoja.querySelector('[data-regalo]');
    var campos = hoja.querySelector('.u-perso__campos');

    /* Actualiza estados sin tocar la estructura: el foco se queda donde está. */
    function actualizar() {
      radios.forEach(function (r) {
        var i = +r.getAttribute('data-oi');
        var hay = valueMatches(p, i, r.value, sel, true);
        r.checked = sel[i] === r.value;
        r.disabled = !hay && !r.checked;
        var lab = r.closest('.u-perso__opcion');
        lab.classList.toggle('is-agotado', !hay);
        lab.querySelector('.u-perso__opcion-estado').hidden = hay;
      });
      var v = findVariant(p, sel);
      var puede = !!(v && v.available);
      btn.disabled = !puede;
      var completo = sel.every(function (s, i) { return s != null || distinctValues(p, i).length < 2; });
      btnTexto.textContent = puede ? 'Agregar' : (v ? 'Agotado' : (completo ? 'No disponible' : 'Elegí una opción'));
      total.textContent = dinero((v ? v.price : p.price) * qty);
      total.hidden = !puede;
      cuantos.value = qty;
      cuantos.textContent = qty;
      menos.disabled = qty <= 1;
    }
    function mostrarError(msg) {
      error.innerHTML = msg ? '<span aria-hidden="true">!</span> ' + esc(msg) : '';
      error.hidden = !msg;
    }

    hoja.querySelector('.u-perso__cerrar').addEventListener('click', cerrar);
    radios.forEach(function (r) {
      r.addEventListener('change', function () { sel[+r.getAttribute('data-oi')] = r.value; mostrarError(''); actualizar(); });
    });
    hoja.querySelectorAll('[data-q]').forEach(function (b) {
      b.addEventListener('click', function () { qty = Math.max(1, Math.min(99, qty + (+b.getAttribute('data-q')))); actualizar(); });
    });
    if (chk) chk.addEventListener('change', function () {
      campos.hidden = !chk.checked;
      mostrarError('');
      if (chk.checked) hoja.querySelector('[data-r="email"]').focus();
    });

    hoja.onsubmit = function (e) {
      e.preventDefault();
      var v = findVariant(p, sel);
      if (!v || !v.available || btn.classList.contains('is-cargando')) return;
      var props = null;
      if (gift && chk && chk.checked) {
        var email = hoja.querySelector('[data-r="email"]');
        var mail = email.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
          email.setAttribute('aria-invalid', 'true');
          mostrarError('Revisá el mail de quien la recibe: ahí le llega la gift card.');
          email.focus();
          return;
        }
        email.removeAttribute('aria-invalid');
        props = {
          'Recipient email': mail,
          '__shopify_send_gift_card_to_recipient': 'on',
          '__shopify_offset': String(new Date().getTimezoneOffset())
        };
        var nombre = hoja.querySelector('[data-r="nombre"]').value.trim();
        var mensaje = hoja.querySelector('[data-r="mensaje"]').value.trim();
        var fecha = hoja.querySelector('[data-r="fecha"]').value;
        if (nombre) props['Recipient name'] = nombre;
        if (mensaje) props['Message'] = mensaje;
        if (fecha) props['Send on'] = fecha;
      }
      var item = { id: v.id, quantity: qty };
      if (props) item.properties = props;
      btn.classList.add('is-cargando');
      btn.setAttribute('aria-busy', 'true');
      agregar([item], { abrirCajon: opciones.abrirCajon }).then(function (res) {
        btn.classList.remove('is-cargando');
        btn.removeAttribute('aria-busy');
        if (!res.ok) { mostrarError(res.error); return; }
        var cb = opciones.alAgregar;
        /* Si no se abre el cajón, el foco vuelve al botón de la carta. */
        cerrar();
        if (cb) cb(res.respuesta, v, qty);
      });
    };

    actualizar();
  }

  /* ── Fichas y tarjetas: reemplazar el botón nativo cuando corresponde ── */
  function handleFromCard(el) {
    var card = el.closest('.card-wrapper, .card, .u-tarjeta, .grid__item, li');
    var a = card && card.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }
  function pageHandle() { var m = location.pathname.match(/\/products\/([^/?#]+)/); return m ? m[1] : null; }

  function enhance() {
    document.querySelectorAll('.quick-add__submit, .quick-add button, product-form button[name="add"]').forEach(function (btn) {
      /* Marca propia: NO usar data-u-perso (ese es el disparador). */
      if (btn.hasAttribute('data-u-perso-listo')) return;
      btn.setAttribute('data-u-perso-listo', '');
      var enFicha = !!btn.closest('product-info, .product__info-wrapper, .product');
      var handle = enFicha ? pageHandle() : handleFromCard(btn);
      if (!handle) return;
      getProduct(handle).then(function (p) {
        if (!needsPicker(p)) return;
        var span = btn.querySelector('span:not(.visually-hidden)') || btn;
        span.textContent = esGiftCard(p) ? 'Elegir el monto' : 'Personalizar';
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          abrir(p, { disparador: btn });
        }, true);
      });
    });

    /* La ficha de v16 compra con el formulario nativo de Dawn (montos de
       la gift card y leche del cappuccino a la vista) y lo avisa con
       data-u-perso-nativo en <product-info>: ahí no se reemplaza nada. */
    var ph = pageHandle();
    if (ph && document.querySelector('[data-u-perso-nativo]')) ph = null;
    if (ph && !document.body.hasAttribute('data-u-perso-ficha')) {
      document.body.setAttribute('data-u-perso-ficha', '');
      getProduct(ph).then(function (p) {
        if (!needsPicker(p)) return;
        document.querySelectorAll('variant-selects, variant-radios').forEach(function (el) { el.style.display = 'none'; });
        /* El formulario de regalo vive dentro del panel: el de la página
           quedaría duplicado y, peor, sin efecto. */
        if (esGiftCard(p)) document.querySelectorAll('recipient-form, .recipient-form').forEach(function (el) { el.style.display = 'none'; });
        var buy = document.querySelector('.product-form__buttons');
        if (buy && !document.querySelector('.u-perso-open')) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'button button--full-width u-perso-open';
          b.setAttribute('aria-haspopup', 'dialog');
          b.textContent = esGiftCard(p) ? 'Elegir el monto' : 'Personalizar';
          b.addEventListener('click', function () { abrir(p, { variante: new URLSearchParams(location.search).get('variant'), disparador: b }); });
          buy.parentNode.insertBefore(b, buy);
          buy.style.display = 'none';
        }
      });
    }
  }

  /* Enlaces que abren el panel desde cualquier lugar (los montos de la
     sección Regalo de la home): data-u-perso="handle" y, opcional,
     data-u-variante="id". El href queda como respaldo sin JavaScript.
     Solo enlaces con un handle de verdad: nada de interceptar botones. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[data-u-perso]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    var handle = a.getAttribute('data-u-perso');
    if (!handle) return;
    e.preventDefault();
    abrir(handle, { variante: a.getAttribute('data-u-variante'), disparador: a }).then(function (abierto) {
      if (!abierto && a.href) window.location = a.href;
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhance);
  else enhance();
  document.addEventListener('shopify:section:load', enhance);

  var pendiente = false;
  new MutationObserver(function (lista) {
    if (pendiente) return;
    for (var i = 0; i < lista.length; i++) {
      if (lista[i].addedNodes.length) {
        pendiente = true;
        requestAnimationFrame(function () { pendiente = false; enhance(); });
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  window.Ultreia = window.Ultreia || {};
  window.Ultreia.personalizar = {
    abrir: abrir,
    cerrar: cerrar,
    necesita: needsPicker,
    tieneOpciones: tieneOpciones,
    agregar: agregar,
    estadoCarrito: estadoCarrito,
    pintarCarrito: pintarCarrito,
    dinero: dinero
  };
})();
